import gql from "graphql-tag";

import type {LoginType} from "@khan/auth-shared";
import type {GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {logUnreachableCode} from "@khan/logging";

import type {removeThirdPartyAuthType} from "./__graphql-types__/index.ts";

const disconnectedMessageKey: (thirdParty: LoginType) => DisconnectMessage = (
    thirdParty,
) => {
    switch (thirdParty) {
        case "google":
            return "successfully_disconnected_google_account";
        case "facebook":
            return "successfully_disconnected_facebook_account";
        case "apple":
            return "successfully_disconnected_apple_account";
        case "microsoft":
            return "successfully_disconnected_microsoft_account";
        default:
            throw logUnreachableCode(
                "Unsupported third party login type",
                thirdParty as never,
            );
    }
};

type DisconnectMessage =
    | "successfully_disconnected_cb_account"
    | "successfully_disconnected_apple_account"
    | "successfully_disconnected_google_account"
    | "successfully_disconnected_facebook_account"
    | "successfully_disconnected_microsoft_account"
    | "cannot_disconnect_last_account"
    | "unable_to_disconnect_account";

export const REMOVE_THIRD_PARTY_AUTH_MUTATION: GqlOperation<
    removeThirdPartyAuthType["response"],
    removeThirdPartyAuthType["variables"]
> = gqlOp(gql`
    mutation removeThirdPartyAuth($oauthType: ThirdPartyOAuthType!) {
        removeThirdPartyAuth(oauthType: $oauthType) {
            error {
                code
            }
        }
    }
`);

export const removeThirdPartyAuth = (
    gqlFetch: GqlFetchFn,
    oauthType: LoginType,
): Promise<DisconnectMessage> => {
    return gqlFetch(REMOVE_THIRD_PARTY_AUTH_MUTATION, {
        variables: {oauthType: oauthType.toUpperCase()},
    })
        .then((data) => {
            if (data.removeThirdPartyAuth && !data.removeThirdPartyAuth.error) {
                return disconnectedMessageKey(oauthType);
            }

            if (
                data?.removeThirdPartyAuth?.error?.code ===
                "CANNOT_DISCONNECT_LAST_ACCOUNT"
            ) {
                return "cannot_disconnect_last_account";
            }
            return "unable_to_disconnect_account";
        })
        .catch((e) => {
            return "unable_to_disconnect_account";
        });
};
